import type {
    B2BTradersChartRow,
    B2BTradersDatasetResponse,
    B2BTradersLeaders,
    B2BTradersSummary,
    ExpoTraderPerformanceRow,
    ExpoTraderSummaryRow,
    PaginationMeta,
} from "../../../lib/api/types";

export const emptyTradersSummary: B2BTradersSummary = {
    traderCount: 0,
    activeTraders: 0,
    orderCount: 0,
    clientCount: 0,
    turnover: 0,
    avgOrderValue: 0,
};

export const emptyTradersLeaders: B2BTradersLeaders = {
    byTurnover: null,
    byOrders: null,
    byClients: null,
};

function round2(value: number) {
    return Math.round(value * 100) / 100;
}

export function aggregatePerformanceToSummary(rows: ExpoTraderPerformanceRow[]): ExpoTraderSummaryRow[] {
    const byUser = new Map<number, ExpoTraderSummaryRow>();

    for (const row of rows) {
        const existing = byUser.get(row.userId);

        if (!existing) {
            byUser.set(row.userId, {
                userId: row.userId,
                dealerId: row.dealerId,
                traderName: row.traderName,
                orderCount: row.orderCount,
                clientCount: row.clientCount,
                completedOrders: row.completedOrders,
                turnover: row.turnover,
                avgOrderValue: row.avgOrderValue,
            });
            continue;
        }

        existing.orderCount += row.orderCount;
        existing.clientCount += row.clientCount;
        existing.completedOrders += row.completedOrders;
        existing.turnover = round2(existing.turnover + row.turnover);
        existing.avgOrderValue =
            existing.orderCount > 0 ? round2(existing.turnover / existing.orderCount) : 0;
    }

    return [...byUser.values()].sort((left, right) => right.turnover - left.turnover || right.orderCount - left.orderCount);
}

export function buildSummary(traders: ExpoTraderSummaryRow[]): B2BTradersSummary {
    let totalOrders = 0;
    let totalTurnover = 0;
    let totalClients = 0;
    let activeTraders = 0;

    for (const row of traders) {
        totalOrders += row.orderCount;
        totalTurnover += row.turnover;
        totalClients += row.clientCount;

        if (row.userId > 0) {
            activeTraders++;
        }
    }

    return {
        traderCount: traders.length,
        activeTraders,
        orderCount: totalOrders,
        clientCount: totalClients,
        turnover: round2(totalTurnover),
        avgOrderValue: totalOrders > 0 ? round2(totalTurnover / totalOrders) : 0,
    };
}

export function buildLeaders(traders: ExpoTraderSummaryRow[]): B2BTradersLeaders {
    if (traders.length === 0) {
        return emptyTradersLeaders;
    }

    let byTurnover = traders[0];
    let byOrders = traders[0];
    let byClients = traders[0];

    for (const row of traders) {
        if (row.turnover > byTurnover.turnover) byTurnover = row;
        if (row.orderCount > byOrders.orderCount) byOrders = row;
        if (row.clientCount > byClients.clientCount) byClients = row;
    }

    return { byTurnover, byOrders, byClients };
}

export function buildChartTop(traders: ExpoTraderSummaryRow[], limit = 10): B2BTradersChartRow[] {
    return traders.slice(0, limit).map((row) => ({
        userId: row.userId,
        traderName: row.traderName,
        turnover: row.turnover,
        orderCount: row.orderCount,
    }));
}

export function paginateTraders(
    traders: ExpoTraderSummaryRow[],
    page: number,
    perPage: number
): { items: ExpoTraderSummaryRow[]; pagination: PaginationMeta } {
    const safePage = Math.max(1, page);
    const safePerPage = Math.max(1, perPage);
    const total = traders.length;
    const lastPage = Math.max(1, Math.ceil(total / safePerPage));
    const offset = (safePage - 1) * safePerPage;
    const items = traders.slice(offset, offset + safePerPage);

    return {
        items,
        pagination: {
            current_page: safePage,
            per_page: safePerPage,
            total,
            last_page: lastPage,
            from: total === 0 ? null : offset + 1,
            to: total === 0 ? null : Math.min(offset + safePerPage, total),
        },
    };
}

export function buildTradersDatasetFromSummaryRows(
    traders: ExpoTraderSummaryRow[],
    options: {
        page: number;
        perPage: number;
        periodFrom?: string;
        periodTo?: string;
        error?: string;
    }
): B2BTradersDatasetResponse {
    const { items, pagination } = paginateTraders(traders, options.page, options.perPage);

    return {
        summary: buildSummary(traders),
        leaders: buildLeaders(traders),
        chartTop: buildChartTop(traders),
        traders: items,
        pagination,
        filters: {
            dateFrom: null,
            dateTo: null,
            traderName: null,
            traderUserId: null,
            page: options.page,
            perPage: options.perPage,
        },
        periodFrom: options.periodFrom,
        periodTo: options.periodTo,
        error: options.error,
        lastUpdatedAt: new Date().toISOString(),
    };
}
